import React from 'react';
import { Link } from 'react-router-dom';
import { Target, Eye, Heart, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useLanguage } from '../context/LanguageContext';

const companyLogo = "https://customer-assets.emergentagent.com/job_bb6c1548-0053-4d1d-9174-d34b5653b3e9/artifacts/43j18h5f_image.png";
const aboutImage = "https://images.unsplash.com/photo-1580982330720-bd5e0fed108b?w=800&q=80";

const AboutPage = () => {
  const { t } = useLanguage();
  const values = t('aboutPage.values');

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, #0d9488 0%, transparent 50%),
              radial-gradient(circle at 75% 75%, #0d9488 0%, transparent 50%)`,
          }} />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <span className="inline-block px-4 py-2 rounded-full bg-blue-500/20 text-blue-300 text-sm font-medium mb-6">
            {t('aboutPage.badge')}
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            {t('aboutPage.title')}
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
            {t('aboutPage.subtitle')}
          </p>
        </div>
      </section>

      {/* About Content */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <span className="text-blue-700 font-semibold text-sm tracking-wider uppercase mb-4 block">
                {t('aboutPage.identityTitle')}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 leading-tight">
                {t('aboutPage.teamTitle')}
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                {t('aboutPage.teamIntro')}
              </p>
              
              <ul className="space-y-4 mb-8">
                {values && values.map((value, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">{value}</span>
                  </li>
                ))}
              </ul>
              
              <Link to="/team">
                <Button className="bg-blue-700 hover:bg-blue-800 text-white px-6 py-3 rounded-xl font-medium transition-all duration-300 hover:shadow-lg hover:shadow-blue-700/25 group">
                  {t('aboutPage.meetTeam')}
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
            
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-blue-200 to-slate-200 rounded-3xl blur-2xl opacity-40" />
              <Card className="relative bg-white border-0 shadow-2xl overflow-hidden">
                <CardContent className="p-0">
                  <div className="h-96 overflow-hidden">
                    <img 
                      src={aboutImage}
                      alt="D&J Engineering Office"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 lg:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center mx-auto mb-8 shadow-xl shadow-blue-700/30">
              <Target className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8">
              {t('aboutPage.missionTitle')}
            </h2>
            <p className="text-xl text-slate-600 leading-relaxed">
              {t('aboutPage.missionContent')}
            </p>
          </div>
        </div>
      </section>

      {/* Values Cards */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-blue-700 font-semibold text-sm tracking-wider uppercase mb-4 block">
              {t('whyUs.sectionTitle')}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
              {t('aboutPage.valuesSection.title')}
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-white border border-slate-200 hover:border-blue-300 hover:shadow-xl transition-all duration-300 group">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-6 group-hover:bg-blue-100 group-hover:scale-110 transition-all duration-300">
                  <Target className="w-8 h-8 text-blue-700" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-4">{t('aboutPage.valuesSection.precision.title')}</h3>
                <p className="text-slate-600 leading-relaxed">
                  {t('aboutPage.valuesSection.precision.desc')}
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-white border border-slate-200 hover:border-blue-300 hover:shadow-xl transition-all duration-300 group">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-6 group-hover:bg-blue-100 group-hover:scale-110 transition-all duration-300">
                  <Eye className="w-8 h-8 text-blue-700" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-4">{t('aboutPage.valuesSection.transparency.title')}</h3>
                <p className="text-slate-600 leading-relaxed">
                  {t('aboutPage.valuesSection.transparency.desc')}
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-white border border-slate-200 hover:border-blue-300 hover:shadow-xl transition-all duration-300 group">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-6 group-hover:bg-blue-100 group-hover:scale-110 transition-all duration-300">
                  <Heart className="w-8 h-8 text-blue-700" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-4">{t('aboutPage.valuesSection.passion.title')}</h3>
                <p className="text-slate-600 leading-relaxed">
                  {t('aboutPage.valuesSection.passion.desc')}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
